import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ProductAnalyticsService } from '@frontendlib/common/services/product-analytics/product-analytics.service';
import { maskForProductAnalytics } from '@frontendlib/common/services/product-analytics/product-analytics-text';
import { ConnectionsModule } from '@modules/connections/connections.module';
import { type Connection } from '@modules/connections/types/connection';
import { SimpleInputComponent } from '@modules/form-controls/components/simple-input/simple-input.component';
import { KnotsModule } from '@modules/knots/knots.module';
import { type Knot } from '@modules/knots/types/knot';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { TagsModule } from '@modules/tags/tags.module';
import { type Tag } from '@modules/tags/types/tag';
import { BoardListStore } from '../../../services/board-list.store';

/**
 * The list's own board search (a pane header's, as in a scoped or embedded list). Laid out on the shared search popover (keyword row, then Cancel / Search);
 * boards are searched locally by title and description, so there is no advanced form or knot suggestions to
 * ADD a marker here (the sidebar's own Knots/Tags panels are where one gets picked, per devdoc/specs/boards.md).
 * The markers a pick there already applied ARE shown here, though — with their own remove control, same as
 * the shared search popover — since they narrow this exact list and, until now, offered no way to see which
 * ones were active or drop just one without clearing the whole search (SBUX-988 review).
 */
@Component({
  selector: 'app-boards-search-popover',
  templateUrl: './boards-search-popover.component.html',
  styleUrls: ['../../../../search/components/search-popover/search-popover.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SimpleInputComponent, Button2Component, KnotsModule, TagsModule, ConnectionsModule],
})
export class BoardsSearchPopoverComponent {
  public readonly width = input(420);
  /** Asked before a search is applied (the host's pending-save and dirty-Details guard); `false` keeps the popover and the typed draft as they are. */
  public readonly beforeApply = input<() => boolean>(() => true);
  public readonly onSearch = output<void>();
  public readonly onCancel = output<void>();

  protected readonly listStore = inject(BoardListStore);
  private readonly searchService = inject(AdvancedSearchService, { optional: true });
  private readonly productAnalytics = inject(ProductAnalyticsService);
  /** Seeded from `effectiveSearch()`, not the store's own local `search` — otherwise a term inherited
   * from another app's search (carried on the shared `AdvancedSearchService`, no local term of its own)
   * opened this field blank while the list was, in fact, still filtered by it (SBUX-988 review). Seeded
   * once, when the popover opens: the field is the user's draft from then on. */
  protected readonly inputControl = new FormControl(this.listStore.effectiveSearch(), { nonNullable: true });

  protected handleSearch(): void {
    if (!this.beforeApply()()) return;
    const trimmed = this.inputControl.value.trim();
    this.listStore.setSearch(trimmed);
    // Submitting a blank field must actually clear an inherited term, not just the local one:
    // `effectiveSearch` falls back to the shared query whenever the local term is empty, so leaving the
    // shared query itself untouched here made a "cleared" search silently come back the moment the local
    // term above was reset to '' (SBUX-988 review). `search('')` only touches `query`, leaving the
    // knot/tag/connection markers — which this popover shows/removes individually — alone.
    if (!trimmed) {
      this.searchService?.search('');
    }
    // Only the Dynamic Panel shows this popover; the rail searches through the shared popup (SBUX-1213).
    this.productAnalytics.track('search_submitted', {
      query: maskForProductAnalytics(trimmed),
      surface: 'dynamic_panel',
    });
    this.onSearch.emit();
  }

  protected handleCancel(): void {
    this.onCancel.emit();
  }

  protected removeKnowledgeItem(item: Knot | Tag | Connection): void {
    this.searchService?.removeKnowledgeItem(item);
  }
}
